import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoadingSpinner from './components/common/LoadingSpinner';
import LoginPage from './pages/LoginPage';
import WaitingListPage from './pages/WaitingListPage';
import StoreSettingsPage from './pages/StoreSettingsPage';
import MenuSettingsPage from './pages/MenuSettingsPage';
import AccountSettingsPage from './pages/AccountSettingsPage';
import ReportPage from './pages/ReportPage';
import CreditsPage from './pages/CreditsPage';
import CreditHistoryPage from './pages/CreditHistoryPage';
import InquiryPage from './pages/InquiryPage';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/** OWNER 만 진입 가능. MEMBER 가 직접 URL 입력 시 대기관리로 리다이렉트 */
function OwnerOnlyRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, role } = useAuth();

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  // role 미확정(레거시 데이터) 시에는 OWNER 로 간주 (기존 단일 owner 가입자 보호)
  if (role === 'MEMBER') {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <WaitingListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/store"
        element={
          <OwnerOnlyRoute>
            <StoreSettingsPage />
          </OwnerOnlyRoute>
        }
      />
      <Route
        path="/settings/menu"
        element={
          <ProtectedRoute>
            <MenuSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/account"
        element={
          <ProtectedRoute>
            <AccountSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route path="/settings" element={<Navigate to="/settings/store" replace />} />
      <Route
        path="/report"
        element={
          <OwnerOnlyRoute>
            <ReportPage />
          </OwnerOnlyRoute>
        }
      />
      <Route
        path="/credits"
        element={
          <OwnerOnlyRoute>
            <CreditsPage />
          </OwnerOnlyRoute>
        }
      />
      <Route
        path="/credits/history"
        element={
          <ProtectedRoute>
            <CreditHistoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inquiry"
        element={
          <ProtectedRoute>
            <InquiryPage />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
